import { useState, useEffect, useRef, useMemo } from 'react';
import { Event } from '../types/event.types';
import { locationCache } from '../services/LocationCacheService';
import { getStableDate } from '../supabase/SmartListenerService';

// ✅ OPTIMISATION ARRAY : Array vide stable pour éviter les re-renders
const EMPTY_EVENTS_ARRAY: Event[] = [];

/**
 * ❤️ HOOK FAVORIS - Version simplifiée sans SmartListenerService
 * Les favoris sont gérés uniquement en IndexedDB local
 */
export const useSmartFavoriteEvents = () => {
  const [rawEvents, setRawEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [cacheSource, setCacheSource] = useState<'indexedDB' | 'none'>('none');
  
  // ✅ OPTIMISATION ANTI-RERENDER : lastUpdate stabilisé avec cache centralisé
  const lastUpdateRef = useRef<Date | null>(null);

  // ✅ OPTIMISATION ARRAY : Mémoriser la signature pour stabilité complète
  const eventsSignature = useMemo(() => {
    const signature = rawEvents.map(event => event.id).sort().join(',');
    // Logger.cache() - silencieux en prod + '...');
    return signature;
  }, [JSON.stringify(rawEvents.map(event => event.id).sort())]);

  // ✅ OPTIMISATION ARRAY : Stabiliser les événements avec signature mémorisée
  const events = useMemo(() => {
    // Logger.listener();
    
    if (rawEvents.length === 0) {
      return EMPTY_EVENTS_ARRAY;
    }
    
    return [...rawEvents];
  }, [eventsSignature]);

  // console.log( - REMPLACÉ'❤️ [useSmartFavoriteEvents] Hook initialisé (mode simplifié)');

  // ✅ FONCTION : Charger les favoris depuis IndexedDB
  const loadFavoriteEvents = async (isInitialLoad: boolean = false) => {
    // Ne mettre loading=true que pour le chargement initial (évite le flash sur refresh)
    if (isInitialLoad) {
      setLoading(true);
    }
    setError(null);
    
    try {
      // console.log( - REMPLACÉ'❤️ [useSmartFavoriteEvents] Chargement favoris');
      
      // Les favoris sont stockés uniquement en IndexedDB, sans userId
      const favoriteEvents = await locationCache.getFavoriteEventsFromDB();
      
      if (favoriteEvents && favoriteEvents.length > 0) {
        // Logger.cache();
        
        // ✅ OPTIMISATION FINALE : Vérifier si les événements ont changé avant de recréer l'array
        const currentSignature = rawEvents.map(e => e.id).sort().join(',');
        const newSignature = favoriteEvents.map(e => e.id).sort().join(',');
        
        if (currentSignature === newSignature) {
          // Logger.info();
          setCacheSource('indexedDB');
          return; // ← SORTIE PRÉCOCE : Ne pas recréer l'array !
        }
        
        // Logger.listener();
        setRawEvents(favoriteEvents);
        setCacheSource('indexedDB');
      } else {
        // console.log( - REMPLACÉ'📭 [useSmartFavoriteEvents] Aucun favori trouvé');
        setRawEvents([]);
        setCacheSource('none');
      }
      
      // ✅ OPTIMISATION ANTI-RERENDER : Utiliser Date stable depuis cache centralisé
      const now = Date.now();
      let stableDate = lastUpdateRef.current;
      
      if (!stableDate || (now - stableDate.getTime()) > 100) {
        stableDate = getStableDate(Math.floor(now / 100) * 100);
        lastUpdateRef.current = stableDate;
      }
      
      setLastUpdate(stableDate);
      
    } catch (err) {
      console.error('❌ [useSmartFavoriteEvents] Erreur chargement:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Chargement initial (avec loading=true)
    loadFavoriteEvents(true);

    // ✅ ÉCOUTER les changements de favoris pour synchronisation
    const handleFavoritesChanged = () => {
      // Logger.listener();
      // Refresh sans loading=true pour éviter le flash
      loadFavoriteEvents(false);
    };

    // ✅ S'ABONNER aux CustomEvents pour synchronisation temps réel
    window.addEventListener('favoritesChanged', handleFavoritesChanged);

    // Cleanup
    return () => {
      window.removeEventListener('favoritesChanged', handleFavoritesChanged);
    };
  }, []);

  return {
    events,
    loading,
    error,
    lastUpdate,
    cacheSource
  };
}; 